import React, { useState, useEffect } from 'react';
import { supportsProviderSync } from '../utils/pharmacies';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import SyncProvidersModal from '../components/SyncProvidersModal';
import PharmacyProviderList from '../components/PharmacyProviderList';
import { fetchPharmacies, createPharmacy, fetchStates, updatePharmacy, deletePharmacy, fetchProviders, syncProviders } from '../services/api';

// Styled Components
const Container = styled.div`
  padding: 30px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding-bottom: 15px;
  border-bottom: 1px solid var(--border-color);
`;

const Title = styled.h1`
  color: var(--text-color);
  margin: 0;
  font-size: 28px;
`;

const Card = styled.div`
  background-color: var(--card-bg);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    transform: translateY(-2px);
  }
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding: 15px;
  background-color: var(--card-bg);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  
  input {
    flex: 1;
    padding: 12px 15px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    font-size: 16px;
    background-color: var(--input-bg);
    color: var(--text-color);
    
    &:focus {
      outline: none;
      border-color: var(--primary-color);
      box-shadow: 0 0 0 3px rgba(var(--primary-color-rgb), 0.2);
    }
  }
`;

const AddButton = styled.button`
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 12px 20px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 15px;

  &:hover {
    background-color: var(--primary-dark);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: 18px;
  color: var(--text-secondary);
`;

const StateBadge = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: 10px;
  background-color: ${props => props.style?.backgroundColor || 'rgba(var(--success-light-rgb), 0.3)'};
  color: ${props => props.style?.color || 'var(--success-color)'};
  border: 1px solid ${props => props.style?.borderColor || 'rgba(var(--success-color-rgb), 0.3)'};
  font-size: 0.7rem;
  margin-right: 0.25rem;
  margin-bottom: 0.25rem;
  font-weight: 600;
  min-width: 25px;
  text-align: center;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.$delete ? 'var(--error-color)' : 'var(--primary-color)'};
  cursor: pointer;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 8px 12px;
  border-radius: 6px;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${props => props.$delete ? 'rgba(var(--error-light-rgb), 0.2)' : 'rgba(var(--primary-light-rgb), 0.2)'};
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

// Modal Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: var(--card-bg);
  border-radius: 8px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
`;

const ModalHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.25rem;
  color: var(--text-color);
`;

const ModalBody = styled.div`
  padding: 1.5rem;
`;

const ModalFooter = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-color);
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border-radius: var(--border-radius);
  border: 1px solid ${props => props.error ? 'var(--error-color)' : 'var(--border-color)'};
  font-size: 1rem;
  background-color: var(--input-bg);
  color: var(--text-color);
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(var(--primary-color-rgb), 0.2);
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-color);
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: var(--border-radius);
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(Button)`
  background-color: var(--primary-color);
  color: white;
  
  &:hover:not(:disabled) {
    background-color: var(--primary-dark);
  }
`;

const SecondaryButton = styled(Button)`
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  
  &:hover:not(:disabled) {
    background-color: var(--bg-hover);
  }
`;

const StateTagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

// State check tags for the form
const StateCheckTag = styled.label`
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background-color: ${props => props.checked ? 'var(--success-color)' : 'var(--bg-secondary)'};
  border: 1px solid ${props => props.checked ? 'var(--success-color)' : 'var(--border-color)'};
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;
  color: ${props => props.checked ? 'white' : 'var(--text-primary)'};
  font-weight: 500;
  
  &:hover {
    background-color: ${props => props.checked ? 'var(--success-dark)' : 'var(--bg-hover)'};
  }
  
  input {
    position: absolute;
    opacity: 0;
    cursor: pointer;
    height: 0;
    width: 0;
  }
`;

const ConfirmationModal = styled(ModalOverlay)``;
const ConfirmationContent = styled(ModalContent)`
  max-width: 500px;
`;
const ConfirmationActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 20px;
  padding: 0 1.5rem 1.5rem;
`;

const CancelButton = styled(SecondaryButton)``;

const DeleteButton = styled(PrimaryButton)`
  background-color: var(--error-color);
  
  &:hover:not(:disabled) {
    background-color: var(--error-dark);
  }
`;

// New styled components for select/unselect all buttons
const StateSelectionActions = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 10px;
  margin-bottom: 12px;
`;

const StateSelectionButton = styled(Button)`
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  background-color: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  
  &:hover:not(:disabled) {
    background-color: var(--bg-hover);
  }
`;

// Notice component for view-only access
const AccessNotice = styled.div`
  background-color: rgba(255, 152, 0, 0.1);
  border: 1px solid rgba(255, 152, 0, 0.3);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #f57c00;
  font-size: 0.9rem;
  
  svg {
    width: 20px;
    height: 20px;
    min-width: 20px;
  }
`;

const PharmaciesPage = () => {
  const [pharmacies, setPharmacies] = useState([]);
  const [states, setStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    address: '',
    states: []
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pharmacyToDelete, setPharmacyToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [hasEditPermission, setHasEditPermission] = useState(false);
  const [pharmacyProviders, setPharmacyProviders] = useState({});
  const [expandedProviders, setExpandedProviders] = useState({});
  const [syncModal, setSyncModal] = useState(null);
  // syncModal shape: { pharmacyId, pharmacyName, phase: 'confirm'|'syncing'|'summary', added: [], removed: [] }

  useEffect(() => {
    getPharmacies();
    getStates();
    
    // Get the current user from localStorage
    const userString = localStorage.getItem('user');
    if (userString) {
      try {
        const user = JSON.parse(userString);
        setCurrentUser(user);
        
        // Set permissions based on user role
        // Only superusers can edit/create/delete
        setHasEditPermission(user.is_superuser);
      } catch (error) {
        console.error('Error parsing user data:', error);
      }
    }
  }, []);

  const getPharmacies = async () => {
    try {
      setLoading(true);
      const data = await fetchPharmacies();
      setPharmacies(data);
      const providerMap = {};
      await Promise.all(
        data.map(async (p) => {
          try {
            const result = await fetchProviders({ pharmacy_id: p.pharmacy_id });
            providerMap[p.pharmacy_id] = { providers: result.providers || [], count: result.count || 0 };
          } catch {
            providerMap[p.pharmacy_id] = { providers: [], count: 0 };
          }
        })
      );
      setPharmacyProviders(providerMap);
    } catch (error) {
      console.error('Error fetching pharmacies:', error);
    } finally {
      setLoading(false);
    }
  };

  const openSyncModal = (pharmacyId, pharmacyName) => {
    setSyncModal({ pharmacyId, pharmacyName, phase: 'confirm', added: [], removed: [] });
  };

  const handleSyncConfirm = async () => {
    const { pharmacyId, pharmacyName } = syncModal;
    setSyncModal(prev => ({ ...prev, phase: 'syncing' }));
    try {
      const syncResult = await syncProviders(pharmacyId, pharmacyName);
      const added = syncResult.created_providers || [];
      const removed = syncResult.removed_providers || [];

      const result = await fetchProviders({ pharmacy_id: pharmacyId });
      setPharmacyProviders(prev => ({ ...prev, [pharmacyId]: { providers: result.providers || [], count: result.count || 0 } }));

      setSyncModal({ pharmacyId, pharmacyName, phase: 'summary', added, removed });
    } catch (error) {
      console.error('Error syncing providers:', error);
      setSyncModal(prev => ({ ...prev, phase: 'summary', added: [], removed: [], error: true }));
    }
  };

  const getStates = async () => {
    try {
      const data = await fetchStates();
      setStates(data);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  // Filter pharmacies based on search term
  const filteredPharmacies = pharmacies.filter(pharmacy => 
    pharmacy.pharmacy_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (pharmacy.pharmacy_address && pharmacy.pharmacy_address.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Get state colors for badges
  const getStateColor = (abbreviation) => {
    if (!abbreviation) return {};
    
    // Color palette for state badges
    const colorPairs = [
      { bg: 'rgb(225, 245, 254)', color: 'rgb(2, 119, 189)', border: 'rgb(129, 212, 250)' },  // Blue
      { bg: 'rgb(255, 253, 231)', color: 'rgb(245, 127, 23)', border: 'rgb(255, 245, 157)' }, // Orange
      { bg: 'rgb(224, 247, 250)', color: 'rgb(0, 131, 143)', border: 'rgb(128, 222, 234)' },  // Teal
      { bg: 'rgb(232, 245, 233)', color: 'rgb(46, 125, 50)', border: 'rgb(165, 214, 167)' },  // Green
      { bg: 'rgb(255, 235, 238)', color: 'rgb(183, 28, 28)', border: 'rgb(239, 154, 154)' },  // Red
      { bg: 'rgb(243, 229, 245)', color: 'rgb(106, 27, 154)', border: 'rgb(206, 147, 216)' }, // Purple
      { bg: 'rgb(232, 234, 246)', color: 'rgb(48, 63, 159)', border: 'rgb(159, 168, 218)' },  // Indigo
      { bg: 'rgb(239, 235, 233)', color: 'rgb(78, 52, 46)', border: 'rgb(188, 170, 164)' },   // Brown
      { bg: 'rgb(225, 245, 254)', color: 'rgb(1, 87, 155)', border: 'rgb(129, 212, 250)' },   // Dark Blue
    ];
    
    // Use the first character of the abbreviation to determine the color
    const charCode = abbreviation.charCodeAt(0);
    const colorIndex = charCode % colorPairs.length;
    
    return {
      backgroundColor: colorPairs[colorIndex].bg,
      color: colorPairs[colorIndex].color,
      borderColor: colorPairs[colorIndex].border
    };
  };

  // Render state badges
  const renderStateBadges = (stateIds) => {
    if (!stateIds || !stateIds.length) return <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>No states assigned</span>;
    
    return stateIds.map((stateId, index) => {
      const state = states.find(s => s.state_id === parseInt(stateId));
      if (!state) return null;
      
      const stateColors = getStateColor(state.state_abbreviation);
      
      return (
        <StateBadge 
          key={`state-${stateId}-${index}`}
          title={state.state_name}
          style={stateColors}
        >
          {state.state_abbreviation}
        </StateBadge>
      );
    });
  };

  const handleAddPharmacy = () => {
    if (!hasEditPermission) return;
    
    setEditMode(false);
    setFormData({
      id: '',
      name: '',
      address: '',
      states: []
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEditPharmacy = (pharmacy) => {
    if (!hasEditPermission) return;
    
    setEditMode(true);
    setFormData({
      id: pharmacy.pharmacy_id,
      name: pharmacy.pharmacy_name,
      address: pharmacy.pharmacy_address || '',
      states: pharmacy.states.map(state => state.toString())
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDeleteConfirmation = (pharmacy) => {
    if (!hasEditPermission) return;
    
    setPharmacyToDelete(pharmacy);
    setShowConfirmation(true);
  };

  const handleDeletePharmacy = async () => {
    if (!pharmacyToDelete || !hasEditPermission) return;
    
    try {
      setSubmitting(true);
      await deletePharmacy(pharmacyToDelete.pharmacy_id);
      
      // Refresh the list
      await getPharmacies();
      
      // Close modal
      setShowConfirmation(false);
      setPharmacyToDelete(null);
    } catch (error) {
      console.error('Error deleting pharmacy:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field if it exists
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleStateChange = (stateId) => {
    setFormData(prev => {
      const currentStates = [...prev.states];
      
      if (currentStates.includes(stateId)) {
        // Remove state if already selected
        return {
          ...prev,
          states: currentStates.filter(id => id !== stateId)
        };
      } else {
        // Add state if not selected
        return {
          ...prev,
          states: [...currentStates, stateId]
        };
      }
    });
    
    // Clear states error if it exists
    if (formErrors.states) {
      setFormErrors(prev => ({
        ...prev,
        states: ''
      }));
    }
  };

  // New functions for select all and unselect all
  const handleSelectAllStates = () => {
    const allStateIds = states.map(state => state.state_id.toString());
    setFormData(prev => ({
      ...prev,
      states: allStateIds
    }));
    
    // Clear states error if it exists
    if (formErrors.states) {
      setFormErrors(prev => ({
        ...prev,
        states: ''
      }));
    }
  };

  const handleUnselectAllStates = () => {
    setFormData(prev => ({
      ...prev,
      states: []
    }));
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Pharmacy name is required';
    }
    
    if (formData.states.length === 0) {
      errors.states = 'At least one state must be selected';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm() || !hasEditPermission) {
      return;
    }
    
    try {
      setSubmitting(true);
      
      const pharmacyData = {
        pharmacy_name: formData.name,
        pharmacy_address: formData.address,
        states: formData.states.map(id => parseInt(id))
      };
      
      if (editMode) {
        // Update existing pharmacy
        await updatePharmacy(formData.id, pharmacyData);
      } else {
        // Create new pharmacy
        await createPharmacy(pharmacyData);
      }
      
      // Refresh the list
      await getPharmacies();
      
      // Close modal
      setShowModal(false);
    } catch (error) {
      console.error('Error saving pharmacy:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container>
      <Header>
        <Title>Pharmacies</Title>
      </Header>
      
      {/* Display notice for view-only users */}
      {!hasEditPermission && (
        <AccessNotice>
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 22C6.477 22 2 17.523 2 12C2 6.477 6.477 2 12 2C17.523 2 22 6.477 22 12C22 17.523 17.523 22 12 22ZM12 20C16.418 20 20 16.418 20 12C20 7.582 16.418 4 12 4C7.582 4 4 7.582 4 12C4 16.418 7.582 20 12 20ZM11 15H13V17H11V15ZM11 7H13V13H11V7Z" fill="currentColor"/>
          </svg>
          <div>
            <strong>View-only access:</strong> You can browse and search pharmacies, but you don't have permission to add, edit, or delete.
          </div>
        </AccessNotice>
      )}
      
      <Card>
        <SearchBar>
          <input 
            type="text" 
            placeholder="Search pharmacies..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {hasEditPermission && (
            <AddButton onClick={handleAddPharmacy}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 6V12M12 12V18M12 12H18M12 12H6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Add Pharmacy
            </AddButton>
          )}
        </SearchBar>
      </Card>
      
      {loading ? (
        <LoadingContainer>Loading pharmacies...</LoadingContainer>
      ) : filteredPharmacies.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-secondary)' }}>
          No pharmacies found.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {filteredPharmacies.map(pharmacy => (
            <Card key={pharmacy.pharmacy_id} style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.75rem', width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ margin: '0 0 0.25rem 0', fontSize: '1.125rem' }}>{pharmacy.pharmacy_name}</h3>
                  <div style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                    {pharmacy.pharmacy_address || 'No address provided'}
                  </div>
                </div>
                
                <ActionButtons>
                  {hasEditPermission && (
                    <ActionButton onClick={() => handleEditPharmacy(pharmacy)}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11 4H4C3.44772 4 3 4.44772 3 5V19C3 19.5523 3.44772 20 4 20H18C18.5523 20 19 19.5523 19 19V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M18.5 2.5C18.8978 2.10217 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10217 21.5 2.5C21.8978 2.89783 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10217 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      Edit
                    </ActionButton>
                  )}
                  {supportsProviderSync(pharmacy.pharmacy_name) && (
                    <ActionButton
                      onClick={() => openSyncModal(pharmacy.pharmacy_id, pharmacy.pharmacy_name)}
                      title="Sync providers from this pharmacy's API"
                      style={{ color: 'var(--text-secondary)' }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="23 4 23 10 17 10" />
                        <polyline points="1 20 1 14 7 14" />
                        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                      </svg>
                      Sync Providers
                    </ActionButton>
                  )}
                </ActionButtons>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', fontWeight: '500', marginBottom: '0.25rem', color: 'var(--text-secondary)' }}>Licensed States</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                    {renderStateBadges(pharmacy.states)}
                  </div>
                </div>

                <PharmacyProviderList
                  pharmacy={pharmacy}
                  pharmacyProviders={pharmacyProviders}
                  expandedProviders={expandedProviders}
                  setExpandedProviders={setExpandedProviders}
                />
              </div>
            </Card>
          ))}
        </div>
      )}
      
      {/* Add/Edit Pharmacy Modal */}
      {showModal && hasEditPermission && (
        <ModalOverlay>
          <ModalContent>
            <form onSubmit={handleSubmit}>
              <ModalHeader>
                <ModalTitle>
                  {editMode ? 'Edit Pharmacy' : 'Add New Pharmacy'}
                </ModalTitle>
                <ActionButton type="button" onClick={handleCloseModal}>
                  ✕
                </ActionButton>
              </ModalHeader>
              
              <ModalBody>
                <FormGroup>
                  <Label htmlFor="name">Pharmacy Name*</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter pharmacy name"
                    error={formErrors.name}
                  />
                  {formErrors.name && <ErrorMessage>{formErrors.name}</ErrorMessage>}
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Enter pharmacy address (optional)"
                  />
                </FormGroup>
                
                <FormGroup>
                  <Label>Operating States*</Label>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                    <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                      Select all states where this pharmacy operates:
                    </p>
                    <StateSelectionActions>
                      <StateSelectionButton 
                        type="button" 
                        onClick={handleSelectAllStates}
                      >
                        Select All
                      </StateSelectionButton>
                      <StateSelectionButton 
                        type="button" 
                        onClick={handleUnselectAllStates}
                      >
                        Unselect All
                      </StateSelectionButton>
                    </StateSelectionActions>
                  </div>
                  
                  {formErrors.states && <ErrorMessage>{formErrors.states}</ErrorMessage>}
                  
                  <StateTagsContainer>
                    {states.map(state => (
                      <StateCheckTag
                        key={state.state_id}
                        checked={formData.states.includes(state.state_id.toString())}
                      >
                        <input
                          type="checkbox"
                          value={state.state_id}
                          checked={formData.states.includes(state.state_id.toString())}
                          onChange={() => handleStateChange(state.state_id.toString())}
                        />
                        {state.state_name}
                      </StateCheckTag>
                    ))}
                  </StateTagsContainer>
                </FormGroup>
              </ModalBody>
              
              <ModalFooter>
                <SecondaryButton type="button" onClick={handleCloseModal}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton type="submit" disabled={submitting}>
                  {submitting ? (editMode ? 'Updating...' : 'Saving...') : (editMode ? 'Update Pharmacy' : 'Save Pharmacy')}
                </PrimaryButton>
              </ModalFooter>
            </form>
          </ModalContent>
        </ModalOverlay>
      )}
      
      {/* Delete Confirmation Modal */}
      {showConfirmation && hasEditPermission && (
        <ConfirmationModal>
          <ConfirmationContent>
            <ModalHeader>
              <ModalTitle>Confirm Delete</ModalTitle>
              <ActionButton type="button" onClick={() => setShowConfirmation(false)}>
                ✕
              </ActionButton>
            </ModalHeader>
            
            <ModalBody>
              <p>Are you sure you want to delete the pharmacy "{pharmacyToDelete?.pharmacy_name}"?</p>
              <p>This action cannot be undone.</p>
            </ModalBody>
            
            <ConfirmationActions>
              <CancelButton onClick={() => setShowConfirmation(false)}>
                Cancel
              </CancelButton>
              <DeleteButton onClick={handleDeletePharmacy} disabled={submitting}>
                {submitting ? 'Deleting...' : 'Delete'}
              </DeleteButton>
            </ConfirmationActions>
          </ConfirmationContent>
        </ConfirmationModal>
      )}

      {/* Sync Providers Modal */}
      <SyncProvidersModal
        syncModal={syncModal}
        onConfirm={handleSyncConfirm}
        onClose={() => setSyncModal(null)}
      />
    </Container>
  );
};

export default PharmaciesPage;